import React, { useState, useEffect } from 'react';
import { FaStar } from 'react-icons/fa';
import './TraderCard.css';
import { seedGradient } from './common/Identicon';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function getInitials(name) {
  const str = typeof name === 'string' ? name : '';
  return str.slice(0, 2).toUpperCase();
}

function getLevelInfo(level) {
  if (level >= 10) return { label: 'Élite',    color: '#f4511e', glow: 'rgba(244,81,30,0.3)'   };
  if (level >= 7)  return { label: 'Experto',  color: '#8e24aa', glow: 'rgba(142,36,170,0.3)'  };
  if (level >= 4)  return { label: 'Avanzado', color: '#0091ea', glow: 'rgba(0,145,234,0.3)'   };
  return             { label: 'Novato',    color: '#7a8a9a', glow: 'rgba(122,138,154,0.25)' };
}

function TraderCard({ trader, offers, rank }) {
  const name = typeof trader === 'string' ? trader : trader?.name ?? 'Trader';

  const [profile, setProfile] = useState(null);

  // ✅ Fetch del perfil real del trader
  useEffect(() => {
    if (!name || name === 'Trader' || name === 'Anónimo') return;
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${encodeURIComponent(name)}`);
        if (res.ok) setProfile(await res.json());
      } catch {}
    };
    fetchProfile();
  }, [name]);

  const traderOffers = (offers || []).filter(o => o.trader === name);
  const rating       = parseFloat(traderOffers[0]?.rating) || parseFloat(profile?.avgRating) || 0;
  const network      = traderOffers[0]?.network || traderOffers[0]?.blockchain || '—';

  const volumeBuy  = traderOffers.filter(o => o.type === 'buy').reduce((s, o)  => s + Number(o.amount), 0);
  const volumeSell = traderOffers.filter(o => o.type === 'sell').reduce((s, o) => s + Number(o.amount), 0);
  const total      = volumeBuy + volumeSell;
  const buyPct     = total > 0 ? Math.round((volumeBuy / total) * 100) : 0;

  // ✅ Nivel y trades reales de la DB — fallback a 1/0 si no cargó
  const level       = profile?.level ?? 1;
  const totalTrades = profile?.trades ?? traderOffers.length;
  const levelInfo   = getLevelInfo(level);
  const avatarColor = seedGradient(profile?.walletAddress || name);
  const isTop3      = rank >= 1 && rank <= 3;

  const fmt = n => Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });

  return (
    <div className={`tc-card ${isTop3 ? 'tc-card--top' : ''}`} style={{ '--level-color': levelInfo.color, '--level-glow': levelInfo.glow }}>

      <div className="tc-header">
        <span className={`fin-rank tc-rankbadge ${rank <= 3 ? `fin-rank--${rank}` : ''}`}>{rank}</span>

        <div className="tc-avatar" style={{ background: profile?.avatar ? 'transparent' : avatarColor }}>
          {profile?.avatar
            ? <img src={profile.avatar} alt={name} />
            : getInitials(name)
          }
        </div>

        <div className="tc-info">
          <span className="tc-name">{name}</span>
          <span className="fin-chip tc-tier" style={{ color: levelInfo.color, borderColor: levelInfo.color + '55' }}>
            <span className="tc-tier-dot" style={{ background: levelInfo.color }} />
            Nv{level} · {levelInfo.label}
          </span>
        </div>

        <div className="tc-rating" title="Calificación">
          <FaStar className="tc-star" />
          <span className="fin-num">{rating > 0 ? rating.toFixed(1) : '—'}</span>
        </div>
      </div>

      <div className="tc-stats">
        <div className="tc-vol">
          <span className="tc-vol-val fin-num">${total > 0 ? fmt(total) : '0'}</span>
          <span className="fin-label tc-vol-label" title="Liquidez publicada en ofertas activas (no es volumen ya operado)">Liquidez</span>
        </div>

        <div className="fin-bar tc-split" title={`Compra ${buyPct}% · Venta ${100 - buyPct}%`}>
          <i style={{ width: `${buyPct}%` }} />
        </div>

        <div className="tc-buy-sell">
          <span className="tc-bs tc-bs-buy"><span className="tc-bs-k">Compra</span> <span className="fin-num">${fmt(volumeBuy)}</span></span>
          <span className="tc-bs tc-bs-sell"><span className="tc-bs-k">Venta</span> <span className="fin-num">${fmt(volumeSell)}</span></span>
        </div>
      </div>

      <div className="tc-footer">
        <span className="fin-chip tc-network">{network}</span>
        <span className="tc-footer-stats">
          <span className="tc-offers" title="Ofertas activas publicadas"><span className="fin-num">{traderOffers.length}</span> <span className="fin-label">ofertas</span></span>
          <span className="tc-trades" title="Operaciones completadas"><span className="fin-num">{totalTrades}</span> <span className="fin-label">ops</span></span>
        </span>
      </div>
    </div>
  );
}

export default TraderCard;
